import { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Plus, X, Check } from 'lucide-react';

/**
 * Searchable category picker, shared by the import review screen and the
 * transaction drill-in modal.
 *
 * Deliberately NOT a native <datalist>: those render in a browser-level popup
 * that Radix's dialog focus trap suppresses, and they give no visual hint that
 * the field is selectable at all.
 *
 * Two Radix interactions this has to work around — both non-obvious, both
 * previously caused this menu to be unusable inside a dialog:
 *
 *   1. Radix's Dialog is modal, so it sets `pointer-events: none` on <body>
 *      and re-enables it only inside the dialog content. This menu portals to
 *      <body> as a SIBLING of the dialog, so it must set `pointerEvents:
 *      'auto'` on itself. Without it, clicks and scrolls pass straight through,
 *      the menu is never the event target, and the click-away handler closes
 *      it on every interaction.
 *   2. Radix watches pointer events outside its content to decide whether to
 *      dismiss. Pointer events are stopped at the menu root so interacting
 *      here can't close the surrounding dialog.
 *
 * Open state is owned by the PARENT (`isOpen` / `onOpenChange`) so that only
 * one menu in a list can ever be open — otherwise they stack on top of one
 * another.
 */
export default function CategorySelect({
  value,
  categories,
  onChange,
  isOpen,
  onOpenChange,
  highlight = false,
  placeholder = 'Uncategorised',
  className = '',
}) {
  const [query, setQuery] = useState('');
  const [coords, setCoords] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const listRef = useRef(null);

  /**
   * Matching is "contains" so you can find "Dan Shopping" by typing "shop",
   * but results are ORDERED prefix-first. That makes typing a letter jump
   * straight to the categories starting with it, which is what you expect
   * from a dropdown, while still finding mid-word matches further down.
   */
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return categories;

    const prefix = [];
    const contains = [];
    for (const c of categories) {
      const lc = c.toLowerCase();
      if (lc.startsWith(q)) prefix.push(c);
      else if (lc.includes(q)) contains.push(c);
    }
    return [...prefix, ...contains];
  }, [categories, query]);

  const canCreate =
    query.trim() &&
    !categories.some(c => c.toLowerCase() === query.trim().toLowerCase());

  // A single flat list of everything selectable, in the same order it renders,
  // so keyboard navigation can never highlight something that isn't on screen.
  const options = useMemo(() => {
    const opts = [];
    if (value) opts.push({ kind: 'clear', value: '', label: 'Clear category' });
    if (canCreate) opts.push({ kind: 'create', value: query.trim(), label: query.trim() });
    filtered.forEach(c => opts.push({ kind: 'category', value: c, label: c }));
    return opts;
  }, [value, canCreate, query, filtered]);

  const openMenu = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const menuHeight = 300;
    const spaceBelow = window.innerHeight - r.bottom;
    setCoords({
      top: spaceBelow < menuHeight ? Math.max(8, r.top - menuHeight - 4) : r.bottom + 4,
      left: r.left,
      width: Math.max(r.width, 220),
    });
    setQuery('');
    setActiveIdx(0);
    onOpenChange(true);
  };

  // When the query changes, jump the highlight to the first real category
  // (skipping the clear/create rows) so type-ahead lands where you expect.
  useEffect(() => {
    if (!query.trim()) { setActiveIdx(0); return; }
    const firstCategory = options.findIndex(o => o.kind === 'category');
    setActiveIdx(firstCategory >= 0 ? firstCategory : 0);
    // options is derived from query; depending on query alone avoids a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Keep the highlighted row inside the scroll viewport.
  useEffect(() => {
    if (!isOpen) return;
    listRef.current
      ?.querySelector(`[data-opt="${activeIdx}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      onOpenChange(false);
    };

    // Capture phase catches the table scrolling beneath us, but it ALSO fires
    // for the menu's own scrollable list — so ignore anything originating
    // inside the menu, or the list closes the moment you try to scroll it.
    const onScroll = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      onOpenChange(false);
    };

    document.addEventListener('mousedown', onPointerDown, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onPointerDown, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [isOpen, onOpenChange]);

  const choose = (val) => {
    onChange(val);
    onOpenChange(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onOpenChange(false);
      btnRef.current?.focus();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => (options.length ? (i + 1) % options.length : 0));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => (options.length ? (i - 1 + options.length) % options.length : 0));
      return;
    }
    if (e.key === 'Home') { e.preventDefault(); setActiveIdx(0); return; }
    if (e.key === 'End') { e.preventDefault(); setActiveIdx(Math.max(0, options.length - 1)); return; }
    if (e.key === 'Enter') {
      e.preventDefault();
      const opt = options[activeIdx];
      if (opt) choose(opt.value);
    }
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => (isOpen ? onOpenChange(false) : openMenu())}
        onKeyDown={e => {
          if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
            e.preventDefault();
            openMenu();
          }
        }}
        className={`w-full h-8 text-sm rounded-md border px-2 bg-white flex items-center justify-between gap-1 text-left transition-colors hover:border-slate-300 ${
          highlight ? 'border-emerald-400 ring-1 ring-emerald-100'
          : value ? 'border-slate-200'
          : 'border-amber-300 bg-amber-50/40'
        } ${className}`}
      >
        <span className={`truncate ${value ? 'text-slate-700' : 'text-amber-600'}`}>
          {value || placeholder}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && coords && createPortal(
        <div
          ref={menuRef}
          className="bg-white rounded-lg border border-slate-200 shadow-xl flex flex-col overflow-hidden"
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            width: coords.width,
            maxHeight: 300,
            zIndex: 2147483000,
            // See the note at the top of this file — without this the menu is
            // invisible to the mouse inside a Radix dialog.
            pointerEvents: 'auto',
          }}
          onMouseDown={e => e.stopPropagation()}
          onPointerDown={e => e.stopPropagation()}
        >
          <div className="p-2 border-b border-slate-100 flex-shrink-0">
            <input
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Type to jump to a category..."
              className="w-full h-7 text-sm px-2 border border-slate-200 rounded outline-none focus:border-emerald-400"
            />
          </div>

          <div
            ref={listRef}
            className="overflow-y-auto overscroll-contain flex-1 min-h-0 py-1"
            onWheel={e => e.stopPropagation()}
          >
            {options.length === 0 && (
              <p className="px-3 py-3 text-xs text-slate-400 text-center">No categories match</p>
            )}

            {options.map((opt, idx) => {
              const active = idx === activeIdx;
              const selected = opt.kind === 'category' && opt.value === value;

              if (opt.kind === 'clear') {
                return (
                  <button
                    key="__clear"
                    data-opt={idx}
                    type="button"
                    onMouseEnter={() => setActiveIdx(idx)}
                    onClick={() => choose('')}
                    className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-1.5 ${
                      active ? 'bg-slate-100 text-slate-600' : 'text-slate-400'
                    }`}
                  >
                    <X className="w-3 h-3" /> Clear category
                  </button>
                );
              }

              if (opt.kind === 'create') {
                return (
                  <button
                    key="__create"
                    data-opt={idx}
                    type="button"
                    onMouseEnter={() => setActiveIdx(idx)}
                    onClick={() => choose(opt.value)}
                    className={`w-full text-left px-3 py-1.5 text-sm text-emerald-700 flex items-center gap-1.5 font-medium ${
                      active ? 'bg-emerald-50' : ''
                    }`}
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Create &ldquo;{opt.label}&rdquo;
                  </button>
                );
              }

              return (
                <button
                  key={opt.value}
                  data-opt={idx}
                  type="button"
                  onMouseEnter={() => setActiveIdx(idx)}
                  onClick={() => choose(opt.value)}
                  className={`w-full text-left px-3 py-1.5 text-sm flex items-center justify-between gap-2 ${
                    active ? 'bg-slate-100' : ''
                  } ${selected ? 'text-emerald-700 font-medium' : 'text-slate-700'}`}
                >
                  <span className="truncate">{opt.label}</span>
                  {selected && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
                </button>
              );
            })}
          </div>

          <div className="px-2 py-1 border-t border-slate-100 flex-shrink-0">
            <p className="text-[10px] text-slate-400">
              Type to jump &middot; &uarr;&darr; navigate &middot; Enter select &middot; Esc close
            </p>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
